/**
 * Internal Login Gate Component
 * 
 * ⚠️ WARNING: This page uses Basic Authentication to access internal backend endpoints.
 * Credentials are validated by the backend, not frontend.
 * 
 * Purpose: Login form for internal sales endpoint with Basic Auth
 */

import React, { useState, useEffect } from "react";
import {
  Paper,
  TextField,
  Button,
  Typography,
  Container,
  CircularProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import axios from "axios";

const useStyles = makeStyles((theme) => ({
  container: {
    marginTop: theme.spacing(8),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
  },
  paper: {
    padding: theme.spacing(4),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    maxWidth: 400,
  },
  form: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  submit: {
    margin: theme.spacing(3, 0, 2),
  },
  error: {
    color: theme.palette.error.main,
    marginTop: theme.spacing(1),
  },
}));

interface InternalLoginGateProps {
  children: React.ReactNode;
}

const STORAGE_KEY = "internal_basic_auth";

/**
 * Login gate for internal pages using Basic Authentication
 * Validates credentials with backend before granting access
 */
const InternalLoginGate: React.FC<InternalLoginGateProps> = ({ children }) => {
  const classes = useStyles();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Check if already authenticated on mount
  useEffect(() => {
    const storedAuth = sessionStorage.getItem(STORAGE_KEY);
    if (storedAuth) {
      // Verify stored credentials are still valid
      verifyCredentials(storedAuth);
    }
  }, []);

  const verifyCredentials = async (basicAuth: string) => {
    const baseUrl = process.env.REACT_APP_API_URL || "https://api.dev.bemyticket.com";
    try {
      await axios.get(`${baseUrl}/_dummy/test_test?size=1`, {
        headers: {
          Authorization: `Basic ${basicAuth}`,
        },
      });
      setIsAuthenticated(true);
    } catch (err) {
      // Invalid credentials, clear storage
      sessionStorage.removeItem(STORAGE_KEY);
      setIsAuthenticated(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    if (!username || !password) {
      setError("Username and password are required");
      setLoading(false);
      return;
    }

    try {
      // Create Basic Auth token
      const basicAuth = btoa(`${username}:${password}`);
      const baseUrl = process.env.REACT_APP_API_URL || "https://api.dev.bemyticket.com";

      // Test credentials with backend
      await axios.get(`${baseUrl}/_dummy/test_test?size=1`, {
        headers: {
          Authorization: `Basic ${basicAuth}`,
        },
      });

      // If successful, store credentials and authenticate
      sessionStorage.setItem(STORAGE_KEY, basicAuth);
      setIsAuthenticated(true);
    } catch (err: any) {
      // Handle authentication errors
      if (err.response?.status === 401) {
        setError("Invalid username or password");
      } else if (err.message === "Network Error") {
        setError("Cannot connect to server. Please check if backend is running.");
      } else {
        setError(err.response?.data?.message || "Authentication failed");
      }
      setPassword("");
    } finally {
      setLoading(false);
    }
  };

  // If authenticated, render children
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // Otherwise, show login form
  return (
    <Container component="main" maxWidth="xs">
      <div className={classes.container}>
        <Paper className={classes.paper} elevation={3}>
          <Typography component="h1" variant="h5">
            Internal Access
          </Typography>
          <Typography variant="caption" color="textSecondary" align="center" style={{ marginTop: 8 }}>
            Basic Authentication Required
          </Typography>
          <form className={classes.form} onSubmit={handleSubmit}>
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              id="username"
              label="Username"
              name="username"
              autoComplete="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
            />
            <TextField
              variant="outlined"
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            {error && (
              <Typography className={classes.error} variant="body2">
                {error}
              </Typography>
            )}
            <Button
              type="submit"
              fullWidth
              variant="contained"
              color="primary"
              className={classes.submit}
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : "Sign In"}
            </Button>
          </form>
        </Paper>
      </div>
    </Container>
  );
};

export default InternalLoginGate;
